import React, { useContext, useEffect, useRef, useState } from "react";
import "@google/model-viewer";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";
import InputAdornment from "@mui/material/InputAdornment";
import { useMaterialUIController } from "../../context";
import { CanvasContext, ICanvasComponent } from "../canvasContext.tsx";
import { GetTitlesCall } from "../../redux/api/title.api";
import { basicURL } from "../../apiManager/APIConfig";

/** Normalize 3D model URL: relative paths become absolute using API base (so model loads from backend). */
function normalizeModelUrl(url: string | null | undefined): string {
  if (url == null || typeof url !== "string") return "";
  const trimmed = url.trim();
  if (!trimmed) return "";
  if (trimmed.startsWith("http://") || trimmed.startsWith("https://")) return trimmed;
  const base = (basicURL || "").replace(/\/api\/?$/, "").replace(/\/$/, "");
  return trimmed.startsWith("/") ? base + trimmed : `${base}/${trimmed}`;
}

export type Model3DOption = {
  label: string;
  modelUrl: string;
  image?: string | null;
};

declare global {
  namespace JSX {
    interface IntrinsicElements {
      "model-viewer": React.DetailedHTMLProps<
        React.HTMLAttributes<HTMLElement> & {
          src?: string;
          alt?: string;
          "camera-controls"?: boolean;
          style?: React.CSSProperties;
        },
        HTMLElement
      >;
    }
  }
}

function getTitleLabel(item: any, field = "title") {
  const list = item?.titleLocalizations?.filter((e: any) => e.field === field) || [];
  return list[0]?.value || item?.title || "";
}

function build3DEntries(titles: any[]): Model3DOption[] {
  const entries: Model3DOption[] = [];
  (titles || []).forEach((tile) => {
    const tileLabel = getTitleLabel(tile, "title");
    (tile.subtitles || []).forEach((sub: any) => {
      const slide = sub?.slides?.[0];
      if (!slide?.id) return;
      const slideLabel = getTitleLabel(sub, "title");
      const file = slide?.Files?.[0] || slide?.files?.[0];
      const modelUrl = file?.url;
      if (!modelUrl) return;
      entries.push({
        label: `${tileLabel} / ${slideLabel}`,
        modelUrl,
        image: sub?.image ?? null,
      });
    });
  });
  return entries;
}

const Model3DElement = (props: ICanvasComponent) => {
  const { content, id, cameraOrbit: savedCameraOrbit, isPlayMode } = props;
  const { actions } = useContext(CanvasContext);
  const [controller] = useMaterialUIController();
  const isDark = controller?.darkMode === true;
  const viewerRef = useRef<HTMLElement | null>(null);
  const saveCameraTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [modelOptions, setModelOptions] = useState<Model3DOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedOption, setSelectedOption] = useState<Model3DOption | null>(null);

  const modelUrl =
    typeof content === "string" ? content : (content as any)?.url || "";
  const displayUrl = normalizeModelUrl(modelUrl);

  useEffect(() => {
    if (isPlayMode || modelOptions.length > 0) return;
    const lang = "en";
    setLoading(true);
    GetTitlesCall(lang, "three_d_model")
      .then((res) => {
        const list = Array.isArray(res) ? res : [];
        setModelOptions(build3DEntries(list));
      })
      .catch(() => setModelOptions([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!displayUrl) return;
    const viewer = viewerRef.current;
    if (!viewer || !savedCameraOrbit) return;
    const el = viewer as unknown as { cameraOrbit?: string };
    if (typeof el.cameraOrbit !== "undefined") {
      el.cameraOrbit = savedCameraOrbit;
    }
  }, [displayUrl, savedCameraOrbit]);

  const handleSelectModel = (option: Model3DOption | null) => {
    if (option) {
      actions?.updateCanvasData({ id, content: option.modelUrl });
      setSelectedOption(option);
      actions?.saveNow?.();
    }
  };

  const handleUrlBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const url = e.target?.value?.trim();
    if (url) {
      actions?.updateCanvasData({ id, content: url });
      actions?.saveNow?.();
    }
  };

  const handleCameraChange = () => {
    const viewer = viewerRef.current as unknown as { cameraOrbit?: string } | null;
    if (!viewer?.cameraOrbit || !id) return;
    if (saveCameraTimeoutRef.current) clearTimeout(saveCameraTimeoutRef.current);
    saveCameraTimeoutRef.current = setTimeout(() => {
      saveCameraTimeoutRef.current = null;
      const orbit = (viewerRef.current as unknown as { cameraOrbit?: string })?.cameraOrbit;
      if (orbit) {
        actions?.updateCanvasData({ id, cameraOrbit: orbit });
        actions?.saveNow?.();
      }
    }, 400);
  };

  if (!modelUrl) {
    if (isPlayMode) {
      return (
        <div
          style={{
            width: "100%",
            height: "100%",
            background: isDark ? "#020617" : "#e5e7eb",
            borderRadius: 6,
          }}
        />
      );
    }
    return (
      <div
        style={{
          width: "100%",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          background: "#e8e8e8",
          padding: 8,
          overflow: "auto",
        }}
      >
        <span style={{ fontSize: 12, marginBottom: 6, display: "block" }}>
          Search and select a 3D model
        </span>
        <Autocomplete
          size="small"
          fullWidth
          loading={loading}
          options={modelOptions}
          value={selectedOption}
          onChange={(_, value) => handleSelectModel(value)}
          getOptionLabel={(opt) => opt?.label ?? ""}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Search by name…"
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <>
                    <InputAdornment position="start">🔍</InputAdornment>
                    {params.InputProps.startAdornment}
                  </>
                ),
              }}
            />
          )}
          renderOption={(props, opt) => (
            <li {...props} key={`${opt.label}-${opt.modelUrl}`}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 1,
                    overflow: "hidden",
                    flexShrink: 0,
                    bgcolor: "grey.200",
                  }}
                >
                  {opt.image ? (
                    <Box
                      component="img"
                      src={opt.image}
                      alt="3D"
                      sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                    />
                  ) : (
                    /* @ts-ignore */
                    <model-viewer
                      src={opt.modelUrl}
                      alt="3D"
                      camera-controls
                      style={{ width: "100%", height: "100%", minWidth: 40, minHeight: 40 }}
                    />
                  )}
                </Box>
                <span style={{ fontSize: 13 }}>{opt.label}</span>
              </Box>
            </li>
          )}
        />
        <span style={{ fontSize: 11, marginTop: 8, marginBottom: 4 }}>Or paste model URL</span>
        <input
          type="url"
          placeholder=".glb / .gltf URL"
          style={{ width: "100%", padding: 6, fontSize: 12, boxSizing: "border-box" }}
          onBlur={handleUrlBlur}
        />
      </div>
    );
  }

  // Use iframe (same as QuizSystem, layouts/titles) so model-viewer loads reliably
  const themeParam = isDark ? "dark" : "light";
  const iframeSrc =
    typeof window !== "undefined"
      ? `${window.location.origin}/model-viewer.html?src=${encodeURIComponent(displayUrl)}&theme=${themeParam}`
      : "";

  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        minHeight: 200,
        overflow: "hidden",
        background: isDark ? "#1a1a1a" : "#f5f5f5",
      }}
    >
      <iframe
        title="3D Model"
        src={iframeSrc}
        style={{
          width: "100%",
          height: "100%",
          minHeight: 200,
          border: "none",
          display: "block",
        }}
      />
    </div>
  );
};

export default Model3DElement;
